import * as React from "react";
import { cn } from "@/lib/utils";

interface SwitchProps extends Omit<React.ComponentProps<"button">, "onChange" | "type"> {
  checked?: boolean;
  defaultChecked?: boolean;
  onCheckedChange?: (checked: boolean) => void;
}

function Switch({
  checked,
  defaultChecked = false,
  onCheckedChange,
  className,
  disabled,
  onClick,
  ...props
}: SwitchProps) {
  const [uncontrolledChecked, setUncontrolledChecked] = React.useState(defaultChecked);
  const isChecked = checked ?? uncontrolledChecked;
  return (
    <button
      type="button"
      role="switch"
      aria-checked={isChecked}
      disabled={disabled}
      className={cn(
        "relative inline-flex h-5 w-9 shrink-0 cursor-pointer items-center rounded-full border border-transparent bg-muted transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring",
        "data-[state=checked]:bg-primary disabled:cursor-not-allowed disabled:bg-muted",
        className,
      )}
      data-state={isChecked ? "checked" : "unchecked"}
      onClick={(event) => {
        onClick?.(event);
        if (event.defaultPrevented) return;
        if (disabled) return;
        const next = !isChecked;
        if (checked === undefined) setUncontrolledChecked(next);
        onCheckedChange?.(next);
      }}
      {...props}
    >
      <span
        aria-hidden
        className="pointer-events-none block size-4 translate-x-0.5 rounded-full bg-background shadow-xs transition-transform data-[state=checked]:translate-x-[1.125rem]"
        data-state={isChecked ? "checked" : "unchecked"}
      />
    </button>
  );
}

export { Switch };
export type { SwitchProps };
